import React, { useEffect, useState } from 'react';
import { AppBar, Avatar, Badge, Box, Button, IconButton, LinearProgress, Toolbar, Typography } from '@mui/material';
import { ChevronDownIcon } from 'src/icons/ChevronDownIcon';
import { COLOR_GRAY, DEFAULT_SPACING, DOUBLE_SPACING } from 'src/theme/style-consts';
import { UserMenu } from 'src/components/AppHeader/UserMenu';
import { HelpMenu } from 'src/components/AppHeader/HelpMenu';
import { BellAltSvgIcon } from 'src/components/AppHeader/icons/BellAltSvgIcon';
import { CircleQuestionSvgIcon } from 'src/components/AppHeader/icons/CircleQuestionIcon';
import { ExitSvgIcon } from 'src/components/AppHeader/icons/ExitSvgIcon';
import { NotificationsPopup } from 'src/components/AppHeader/Notifications/NotificationsPopup';
import { useGetProjectNotificationsQuery } from 'src/api/v2/project-v2-ApiSlice';
import { theme } from 'src/theme/mui-theme';
import { getFrontendURL } from 'src/utils/url';
import { UplanIcon } from 'src/components/AppHeader/icons/UplanIcon';
import CalendarIcon from 'src/components/AppHeader/icons/calendarIcon.png';
import UpgradeButton from "src/components/AppHeader/UpgradeButton";
import { ts, ts2 } from "src/utils/translate";
import { useSelector } from "react-redux";
import { RootState } from "src/app/store";
import { useGetUserInfoQuery } from "src/api/v2/user-v2-ApiSlice";
import { UserInfoResponse } from "src/model/api/get-user-info-response.model";
import dayjs from "dayjs";
import 'dayjs/locale/hu';
import 'dayjs/locale/de';
import 'dayjs/locale/nl';
import 'dayjs/locale/ro';
import 'dayjs/locale/cs';
import 'dayjs/locale/sk';
import 'dayjs/locale/sr';
import 'dayjs/locale/fr';
import { GetProjectNotificationsResponseItem } from "src/model/api/get-project-notifications-response.model";
import { SubscriptionModal } from "src/components/AppHeader/SubscriptionModal";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { showSnackBar } from "src/api/ui-slice";
import { useSaveStripeCheckoutMutation, useUnactivateStripeSessionMutation } from "src/api/v1/payment-ApiSlice";
import { useAppDispatch } from "src/app/hooks";

interface StripeRedirectionProps {
    dispatch: ReturnType<typeof useAppDispatch>;
    locationPath: ReturnType<typeof useLocation>;
    searchParams: URLSearchParams;
    saveStripeCheckout: ReturnType<typeof useSaveStripeCheckoutMutation>[0];
    unactivateStripeSession: ReturnType<typeof useUnactivateStripeSessionMutation>[0];
}

const handleStripeRedirection = ({
     dispatch,
     locationPath,
     searchParams,
     saveStripeCheckout,
     unactivateStripeSession
}: StripeRedirectionProps) => {
    const hash = locationPath.hash;

    const sessionId = searchParams.get("session_id");

    if (!sessionId) return;

    if (hash === "#success") {
        saveStripeCheckout({ sessionId })
            .then(() => {
                unactivateStripeSession({ sessionId })
                    .finally(() => {
                        dispatch(showSnackBar({
                            text: ts("you_can_use_pro", "You can now use Uplan with Pro features"),
                            severity: 'success'
                        }));
                    });
            });
    } else if (hash === "#cancel") {
        unactivateStripeSession({ sessionId })
            .then(() => {
                dispatch(showSnackBar({
                    text: ts("please_complete_payment_to_use_pro", "Please complete the payment to use Uplan Pro"),
                    severity: 'error'
                }));
            });
    }
};

function AppHeader() {

  const { loadingType, loadingText, progress, activeRequestsCount } = useSelector((state: RootState) => state.ui);

  const { data: userInfo } = useGetUserInfoQuery();

    useEffect(() => {
        if (userInfo && userInfo.siteLanguage !== "") {
            dayjs.locale(userInfo.siteLanguage.substring(0,2));
        }
    }, [userInfo]);

  const projectNotificationsResult = useGetProjectNotificationsQuery(undefined,
      {
        pollingInterval: 30000
      });

  const [userMenuAnchorEl, setUserMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };
  const [helpMenuAnchorEl, setHelpMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleHelpMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setHelpMenuAnchorEl(event.currentTarget);
  };
  const [notificationsPopupAnchorEl, setNotificationsPopupAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleNotificationsPopupClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsPopupAnchorEl(event.currentTarget);
  };

  const handleLogoutClick = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = `${getFrontendURL()}/site/logout`;
  };

  const hasNotification = projectNotificationsResult.data
      ? projectNotificationsResult.data.notifications.some(
          (n: GetProjectNotificationsResponseItem) => n.hasUserAcceptedOccupation === 0
      )
      : false;

  let userName: string | null = null;
  let userAvatarLink: string | null = null;
  let userInitials: string | null = null;
  if (userInfo) {
    userName = userInfo.firstName;
    userInitials = userInfo.firstName.substring(0, 1) + userInfo.lastName.substring(0, 1);
  }

  if (userInfo && userInfo.userAvatarLink !== '') {
    userAvatarLink = `${getFrontendURL()}/images/avatars/${userInfo.userAvatarLink}`;
  }

  function getSubscriptionColors(subscriptionStatus: UserInfoResponse) {
    let backgroundColor = 'none';
    let textColor = 'inherit';

    if (subscriptionStatus) {
      if (subscriptionStatus.hasPro) {
        backgroundColor = 'none';

        if (subscriptionStatus.daysLeft !== null && subscriptionStatus.daysLeft <= 3) {
          backgroundColor = 'linear-gradient(270deg, rgba(255, 248, 225, 0.20) 0.96%, #FFF8E1 18.21%, rgba(255, 200, 113, 0.70) 50.48%, #FFF8E1 75.24%, rgba(255, 248, 225, 0.20) 100%)';
          textColor = '#FF7043';
        } else {
          textColor = 'inherit';
        }

      } else {
        backgroundColor = 'linear-gradient(270deg, rgba(255, 225, 225, 0.20) 0.96%, #FFE1E1 18.21%, rgba(255, 113, 113, 0.70) 50.48%, #FFE1E1 75.24%, rgba(255, 225, 225, 0.20) 100%)';
        textColor = 'inherit';
      }
    }

    return { backgroundColor, textColor };
  }

  const { backgroundColor, textColor } = getSubscriptionColors(userInfo!);
  const [ isSubscriptionModalOpen, setIsSubscriptionModalOpen ] = useState(false);

  let message = null;
  let showIcon = false;

    const dispatch = useAppDispatch();
    const locationPath = useLocation();
    const [searchParams] = useSearchParams();
    const [saveStripeCheckout] = useSaveStripeCheckoutMutation();
    const [unactivateStripeSession] = useUnactivateStripeSessionMutation();

    useEffect(() => {
        handleStripeRedirection({
            dispatch,
            locationPath,
            searchParams,
            saveStripeCheckout,
            unactivateStripeSession
        });
    }, [dispatch, locationPath, saveStripeCheckout, searchParams, unactivateStripeSession]);

  if (userInfo) {
    if (!userInfo.daysLeft || (userInfo.daysLeft <= 3)) {
      showIcon = true;
      if (userInfo.daysLeft !== null && userInfo.daysLeft > 0) {
        message = ts2('your_subscription_expire_in_days', "Your subscription will expire in {days} day(s)", {
          days: userInfo.daysLeft.toString()
        });
      } else {
        message = ts("your_subscription_expired", "Your subscription expired");
      }
    }
  }

  const navigate = useNavigate();

  return (
      <>
        <AppBar position="sticky" sx={{background: backgroundColor, zIndex: (theme) => theme.zIndex.drawer + 1}}>
          <Toolbar>
            <UplanIcon sx={{color: '#000', width: '40px', height: '40px', cursor: 'pointer'}} onClick={() => navigate("/projects")}/>
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>

              {loadingType === 'simple' && activeRequestsCount > 0 && (
                  <Box sx={{ width: '300px', marginRight: '100px' }}>
                    <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main }}>
                      {loadingText ?? ts("in_progress_loading", "In Progress...")}
                    </Typography>
                    <LinearProgress sx={{ height: 10 }}/>
                  </Box>
              )}

              {loadingType === 'progress' && progress !== undefined && (
                  <Box sx={{ width: '300px', marginRight: '100px' }}>
                    <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main }}>
                      {loadingText
                          ? `${loadingText} ${progress}%`
                          : ts2('percent_to_complete', "{progress}% to complete", {
                            progress: progress.toString()
                          })}
                    </Typography>
                    <LinearProgress variant="determinate" value={progress} sx={{ height: 10 }}/>
                  </Box>
              )}
            </Box>

            {message && (
                <>
                  <Typography variant="subtitle1" sx={{ color: textColor }}>{message}</Typography>
                  {showIcon && (
                      <img
                          src={CalendarIcon}
                          alt="Calendar Icon"
                          style={{
                            height: '50px',
                            width: 'auto',
                            opacity: 0.5,
                            marginRight: '40px'
                          }}
                      />
                  )}
                </>
            )}

            <UpgradeButton userInfo={ userInfo } setIsSubscriptionModalOpen={setIsSubscriptionModalOpen} />
            <IconButton sx={{mr: DEFAULT_SPACING}} onClick={handleHelpMenuClick}>
              <CircleQuestionSvgIcon sx={{color: COLOR_GRAY}}/>
            </IconButton>
            <Badge
                badgeContent={<></>}
                color='error'
                invisible={!hasNotification}
                sx={{
                    "& .MuiBadge-badge": {
                        minWidth: "12px",
                        height: "12px",
                        top: "8px",
                        right: "4px"
                    },
                }}
            >
              <IconButton sx={{p: 0}} onClick={handleNotificationsPopupClick}>
                <BellAltSvgIcon sx={{color: COLOR_GRAY}}/>
              </IconButton>
            </Badge>
            {userAvatarLink == null ? (
                <Avatar sx={{ml: DOUBLE_SPACING}} onClick={handleUserMenuClick}>{userInitials}</Avatar>
            ) : (
                <Avatar sx={{ml: DOUBLE_SPACING}} onClick={handleUserMenuClick} src={userAvatarLink!!}></Avatar>
            )}
            {userName != null ? (
                <Button
                    sx={{mr: DEFAULT_SPACING, color: theme.palette.text.secondary}}
                    onClick={handleUserMenuClick}
                    variant="text"
                    endIcon={<ChevronDownIcon sx={{ fontSize: 20 }}/>}
                >
                    {userName}
                </Button>
            ) : null}
            <IconButton onClick={handleLogoutClick}>
              <ExitSvgIcon color='primary'/>
            </IconButton>
          </Toolbar>
        </AppBar>
        <UserMenu anchorEl={userMenuAnchorEl} handleClose={() => setUserMenuAnchorEl(null)}/>
        <HelpMenu anchorEl={helpMenuAnchorEl} handleClose={() => setHelpMenuAnchorEl(null)}/>
        <NotificationsPopup
            anchorEl={notificationsPopupAnchorEl}
            handleClose={() => setNotificationsPopupAnchorEl(null)}
        />

        <SubscriptionModal open={isSubscriptionModalOpen} onClose={() => setIsSubscriptionModalOpen(false)} />
      </>
  );
}

export default AppHeader;
